(() => {
  'use strict';

  /**
   * CarPoster Attribution v2
   *
   * - Keeps a bounded first-touch journey on the customer's device for up to
   *   90 days (subject to browser privacy/storage policies).
   * - Counts sessions using a 30-minute inactivity boundary.
   * - Keeps only the latest 10 session-start timestamps; the total session
   *   count can be higher.
   * - Uses Shopify Customer Privacy as the source of truth for consent.
   * - Does not persist attribution or read ad-platform identifiers until marketing processing is allowed.
   * - Refreshes Meta browser/click identifiers only after consent, including
   *   delayed re-checks while Shopify/Meta pixels initialize. If Meta's _fbc
   *   cookie is unavailable, derives the documented fbc value from a real
   *   fbclid captured after consent. _fbp is never fabricated.
   * - Preserves OpenAI Ads oppref after consent so server-side order_created
   *   events can be matched back to eligible ChatGPT ad clicks.
   * - Never submits anything itself. The checkout reads this snapshot and sends
   *   it as optional telemetry with the order request.
   */

  const VERSION = 2;
  const STORAGE_KEY = 'cp_attribution_v2';
  const LEGACY_SESSION_KEY = 'cp_attribution_v1';
  const TTL_MS = 90 * 24 * 60 * 60 * 1000;
  const SESSION_GAP_MS = 30 * 60 * 1000;
  const MAX_SESSION_STARTS = 10;
  const MAX_URL_LENGTH = 2048;
  const MAX_VALUE_LENGTH = 512;
  const POST_CONSENT_REFRESH_DELAYS_MS = [250, 1000, 3000, 8000];
  let postConsentRefreshTimers = [];

  const UTM_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'utm_id',
  ];

  function clamp(value, maxLength = MAX_VALUE_LENGTH) {
    const text = String(value ?? '').trim();
    if (!text) return null;
    return text.slice(0, maxLength);
  }

  function safeUrl(rawUrl) {
    try {
      const url = new URL(String(rawUrl || ''), window.location.origin);
      url.hash = '';
      return clamp(url.toString(), MAX_URL_LENGTH);
    } catch (_) {
      return clamp(rawUrl, MAX_URL_LENGTH);
    }
  }

  function safeIso(value) {
    const time = Date.parse(String(value || ''));
    return Number.isFinite(time) ? new Date(time).toISOString() : null;
  }

  function nowIso() {
    return new Date().toISOString();
  }

  function readCookie(name) {
    try {
      const prefix = `${encodeURIComponent(name)}=`;
      const parts = String(document.cookie || '').split(';');
      for (const part of parts) {
        const trimmed = part.trim();
        if (!trimmed.startsWith(prefix)) continue;
        return clamp(decodeURIComponent(trimmed.slice(prefix.length)));
      }
    } catch (_) {}
    return null;
  }

  function deriveFbcFromTouch(touch) {
    const fbclid = clamp(touch?.fbclid, 512);
    if (!fbclid) return null;

    const capturedMs = Date.parse(String(touch?.capturedAt || ''));
    const timestampMs = Number.isFinite(capturedMs) ? capturedMs : Date.now();
    return clamp(`fb.1.${timestampMs}.${fbclid}`, 1024);
  }

  function privacyState() {
    const privacy = window.Shopify?.customerPrivacy;

    // Consent is deliberately fail-closed for our custom attribution layer.
    // Shopify's API is loaded asynchronously below; until it is ready, we do
    // not persist attribution and we do not read Meta cookies.
    if (!privacy) {
      return {
        apiAvailable: false,
        analytics: null,
        marketing: null,
        saleOfData: null,
        canCapture: false,
        canSendMeta: false,
      };
    }

    let analytics = null;
    let marketing = null;
    let saleOfData = null;

    try {
      if (typeof privacy.analyticsProcessingAllowed === 'function') {
        analytics = Boolean(privacy.analyticsProcessingAllowed());
      }
    } catch (_) {}

    try {
      if (typeof privacy.marketingAllowed === 'function') {
        marketing = Boolean(privacy.marketingAllowed());
      }
    } catch (_) {}

    try {
      if (typeof privacy.saleOfDataAllowed === 'function') {
        saleOfData = Boolean(privacy.saleOfDataAllowed());
      }
    } catch (_) {}

    return {
      apiAvailable: true,
      analytics,
      marketing,
      saleOfData,
      // Shopify defines marketing processing to include attribution and
      // targeted advertising. That is the relevant permission for this record.
      canCapture: marketing === true,
      // If a sale/sharing opt-out applies, don't send data to Meta. In regions
      // where this signal isn't applicable/available it can remain null.
      canSendMeta: marketing === true && saleOfData !== false,
    };
  }

  function consentSnapshot(privacy) {
    return {
      source: 'SHOPIFY_CUSTOMER_PRIVACY',
      marketingAllowed: privacy.marketing === true,
      analyticsAllowed: typeof privacy.analytics === 'boolean' ? privacy.analytics : null,
      saleOfDataAllowed: typeof privacy.saleOfData === 'boolean' ? privacy.saleOfData : null,
      checkedAt: nowIso(),
    };
  }

  function storageRemove() {
    try { window.localStorage.removeItem(STORAGE_KEY); } catch (_) {}
  }

  function isExpired(record) {
    if (!record) return true;
    const expiresAt = Date.parse(String(record?.journey?.expiresAt || ''));
    if (Number.isFinite(expiresAt)) return Date.now() >= expiresAt;

    const firstAt = Date.parse(String(record?.journey?.firstVisitAt || record?.firstTouch?.capturedAt || ''));
    return Number.isFinite(firstAt) ? Date.now() >= firstAt + TTL_MS : false;
  }

  function readStored() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || parsed.version !== VERSION) return null;
      if (isExpired(parsed)) {
        storageRemove();
        return null;
      }
      return parsed;
    } catch (_) {
      return null;
    }
  }

  function writeStored(value) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
      return true;
    } catch (_) {
      return false;
    }
  }

  function currentQueryData() {
    const params = new URLSearchParams(window.location.search || '');
    const utm = {};

    UTM_KEYS.forEach((key) => {
      const value = clamp(params.get(key));
      if (value) utm[key] = value;
    });

    return {
      utm,
      fbclid: clamp(params.get('fbclid')),
      oppref: clamp(params.get('oppref'), 2048),
    };
  }

  function clientContext() {
    let timeZone = null;
    try {
      timeZone = clamp(Intl.DateTimeFormat().resolvedOptions().timeZone, 128);
    } catch (_) {}

    return {
      timeZone,
      utcOffsetMinutes: -new Date().getTimezoneOffset(),
    };
  }

  function buildTouch({ includeMarketing = true } = {}) {
    const query = currentQueryData();
    return {
      capturedAt: nowIso(),
      landingUrl: safeUrl(window.location.href),
      landingPath: clamp(`${window.location.pathname || '/'}${window.location.search || ''}`, MAX_URL_LENGTH),
      referrer: safeUrl(document.referrer || ''),
      utm: query.utm,
      fbclid: includeMarketing ? query.fbclid : null,
      oppref: includeMarketing ? query.oppref : null,
    };
  }

  function hostnameOf(value) {
    try {
      return new URL(String(value || ''), window.location.origin).hostname.toLowerCase().replace(/^www\./, '');
    } catch (_) {
      return '';
    }
  }

  function isExternalReferrer(touch) {
    const refHost = hostnameOf(touch?.referrer);
    const landingHost = hostnameOf(touch?.landingUrl);
    return Boolean(refHost && landingHost && refHost !== landingHost);
  }

  function hasAcquisitionSignal(touch) {
    return Boolean(
      touch && (
        touch.fbclid ||
        touch.oppref ||
        Object.keys(touch.utm || {}).length ||
        isExternalReferrer(touch)
      )
    );
  }

  function stripMarketing(record) {
    if (!record) return record;
    record.meta = { fbp: null, fbc: null };
    record.openai = { oppref: null, capturedAt: null };
    if (record.firstTouch) { record.firstTouch.fbclid = null; record.firstTouch.oppref = null; }
    if (record.lastTouch) { record.lastTouch.fbclid = null; record.lastTouch.oppref = null; }
    return record;
  }

  function migrateLegacyRecord(privacy) {
    try {
      if (window.localStorage.getItem(STORAGE_KEY)) return null;
      const raw = window.sessionStorage.getItem(LEGACY_SESSION_KEY);
      if (!raw) return null;
      const legacy = JSON.parse(raw);
      if (!legacy || legacy.version !== 1 || !legacy.firstTouch) return null;

      const firstVisitAt = safeIso(legacy.firstTouch.capturedAt) || nowIso();
      const firstMs = Date.parse(firstVisitAt);
      if (!Number.isFinite(firstMs) || Date.now() >= firstMs + TTL_MS) return null;

      const migrated = {
        version: VERSION,
        consent: consentSnapshot(privacy),
        firstTouch: legacy.firstTouch,
        lastTouch: hasAcquisitionSignal(legacy.firstTouch) ? legacy.firstTouch : null,
        journey: {
          firstVisitAt,
          lastVisitAt: nowIso(),
          lastActivityAt: nowIso(),
          lastSessionAt: firstVisitAt,
          sessionCount: 1,
          sessionStarts: [firstVisitAt],
          expiresAt: new Date(firstMs + TTL_MS).toISOString(),
        },
        meta: {
          fbp: clamp(legacy?.meta?.fbp),
          fbc: clamp(legacy?.meta?.fbc),
        },
        openai: { oppref: null, capturedAt: null },
        client: clientContext(),
        updatedAt: nowIso(),
      };

      writeStored(migrated);
      try { window.sessionStorage.removeItem(LEGACY_SESSION_KEY); } catch (_) {}
      return migrated;
    } catch (_) {
      return null;
    }
  }

  function createRecord(privacy) {
    const firstTouch = buildTouch({ includeMarketing: true });
    const firstVisitAt = firstTouch.capturedAt;
    const firstMs = Date.parse(firstVisitAt);

    return {
      version: VERSION,
      consent: consentSnapshot(privacy),
      firstTouch,
      lastTouch: hasAcquisitionSignal(firstTouch) ? { ...firstTouch, utm: { ...(firstTouch.utm || {}) } } : null,
      journey: {
        firstVisitAt,
        lastVisitAt: firstVisitAt,
        lastActivityAt: firstVisitAt,
        lastSessionAt: firstVisitAt,
        sessionCount: 1,
        sessionStarts: [firstVisitAt],
        expiresAt: new Date(firstMs + TTL_MS).toISOString(),
      },
      meta: {
        fbp: null,
        fbc: null,
      },
      openai: {
        oppref: firstTouch.oppref || readCookie('__oppref') || null,
        capturedAt: firstTouch.oppref ? firstTouch.capturedAt : null,
      },
      client: clientContext(),
      updatedAt: firstVisitAt,
    };
  }

  function refresh({ forceCreate = false } = {}) {
    const privacy = privacyState();

    if (!privacy.apiAvailable || !privacy.canCapture) {
      // Unknown/not-yet-loaded consent means: do nothing. An explicit marketing
      // denial means: remove our own non-essential attribution record.
      if (privacy.apiAvailable && privacy.marketing === false) clear();
      return null;
    }

    let stored = readStored() || migrateLegacyRecord(privacy);
    if (!stored && forceCreate) stored = createRecord(privacy);
    if (!stored) return null;

    const now = Date.now();
    const nowString = new Date(now).toISOString();
    stored.journey = stored.journey || {};

    const lastActivityMs = Date.parse(String(stored.journey.lastActivityAt || ''));
    const isNewSession = !Number.isFinite(lastActivityMs) || (now - lastActivityMs) > SESSION_GAP_MS;

    if (isNewSession) {
      const existingCount = Math.max(1, Number(stored.journey.sessionCount) || 1);
      stored.journey.sessionCount = existingCount + 1;
      stored.journey.lastSessionAt = nowString;

      const starts = Array.isArray(stored.journey.sessionStarts)
        ? stored.journey.sessionStarts.filter((value) => safeIso(value)).map((value) => safeIso(value))
        : [];
      starts.push(nowString);
      stored.journey.sessionStarts = starts.slice(-MAX_SESSION_STARTS);
    }

    stored.journey.lastVisitAt = nowString;
    stored.journey.lastActivityAt = nowString;

    const currentTouch = buildTouch({ includeMarketing: true });
    if (hasAcquisitionSignal(currentTouch)) {
      stored.lastTouch = currentTouch;
    }

    stored.client = clientContext();
    stored.consent = consentSnapshot(privacy);
    stored.meta = stored.meta || {};
    stored.openai = stored.openai || { oppref: null, capturedAt: null };

    const openAiOppref = currentTouch.oppref || readCookie('__oppref') || null;
    if (openAiOppref) {
      stored.openai.oppref = openAiOppref;
      stored.openai.capturedAt = currentTouch.oppref ? currentTouch.capturedAt : (stored.openai.capturedAt || nowString);
    }

    // _fbp is only trustworthy when Meta actually created it. Never fabricate it.
    stored.meta.fbp = readCookie('_fbp') || stored.meta.fbp || null;

    // Meta permits fbc to be constructed from a genuine fbclid when the _fbc
    // cookie itself is unavailable. Prefer the real cookie, then the latest
    // consented Meta click, then the consented first touch.
    stored.meta.fbc =
      readCookie('_fbc') ||
      deriveFbcFromTouch(stored.lastTouch) ||
      deriveFbcFromTouch(stored.firstTouch) ||
      stored.meta.fbc ||
      null;

    stored.updatedAt = nowString;
    writeStored(stored);
    return stored;
  }

  function schedulePostConsentRefreshes() {
    postConsentRefreshTimers.forEach((timer) => {
      try { window.clearTimeout(timer); } catch (_) {}
    });
    postConsentRefreshTimers = [];

    POST_CONSENT_REFRESH_DELAYS_MS.forEach((delay) => {
      const timer = window.setTimeout(() => {
        const privacy = privacyState();
        if (privacy.apiAvailable && privacy.canCapture) {
          refresh({ forceCreate: false });
        }
      }, delay);
      postConsentRefreshTimers.push(timer);
    });
  }

  function capture() {
    return refresh({ forceCreate: true });
  }

  function get() {
    const privacy = privacyState();
    if (!privacy.apiAvailable || !privacy.canCapture) {
      if (privacy.apiAvailable && privacy.marketing === false) clear();
      return null;
    }
    const existing = readStored() || migrateLegacyRecord(privacy);
    if (existing) return refresh({ forceCreate: false }) || existing;
    return capture();
  }

  function clear() {
    storageRemove();
    postConsentRefreshTimers.forEach((timer) => {
      try { window.clearTimeout(timer); } catch (_) {}
    });
    postConsentRefreshTimers = [];
    try { window.sessionStorage.removeItem(LEGACY_SESSION_KEY); } catch (_) {}
  }

  window.CarPosterAttribution = Object.freeze({
    version: VERSION,
    retentionDays: 90,
    sessionGapMinutes: 30,
    consentAware: true,
    get,
    refresh: () => refresh({ forceCreate: false }),
    capture,
    clear,
    privacy: privacyState,
  });

  function applyCurrentConsent() {
    const privacy = privacyState();
    if (!privacy.apiAvailable) return null;
    if (privacy.marketing === false) {
      clear();
      return null;
    }
    if (privacy.canCapture) {
      const record = capture();
      // Shopify app pixels that require marketing consent may initialize just
      // after this consent event. Re-check Meta's first-party identifiers for
      // a few seconds so a race at consent time doesn't leave fbp/fbc empty.
      schedulePostConsentRefreshes();
      return record;
    }
    return null;
  }

  function loadCustomerPrivacyApi() {
    if (window.Shopify?.customerPrivacy) {
      applyCurrentConsent();
      return;
    }

    const loader = window.Shopify?.loadFeatures;
    if (typeof loader !== 'function') return;

    try {
      loader(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        (error) => {
          if (!error) applyCurrentConsent();
        },
      );
    } catch (_) {}
  }

  // Shopify's banner writes consent into the Customer Privacy API. We only
  // observe that state; we never grant consent on the visitor's behalf.
  loadCustomerPrivacyApi();
  document.addEventListener('DOMContentLoaded', loadCustomerPrivacyApi, { once: true });
  document.addEventListener('shopifyCustomerPrivacyApiLoaded', applyCurrentConsent);
  document.addEventListener('visitorConsentCollected', applyCurrentConsent);
  window.addEventListener('pageshow', loadCustomerPrivacyApi);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      const privacy = privacyState();
      if (privacy.apiAvailable && privacy.marketing === false) clear();
      else if (privacy.canCapture) refresh({ forceCreate: false });
    }
  });
})();
